import { Callout } from '@liveauctioneers/hammer-ui-core/callout';
import { Loading } from '@liveauctioneers/hammer-ui-core/loading';
import { Radio } from '@liveauctioneers/hammer-ui-core/radio';
import { H3 } from '@liveauctioneers/hammer-ui-core/text';
import { CreditCardOnFile } from '@liveauctioneers/hammer-ui-payments/checkout';
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import type { BankAccount } from '@/types/bankAccount';
import type { CreditCard } from '@/types/CreditCard';

import BankAccountStatus from '@/enums/bankAccountStatus';
import useIsServerSideRendered from '@/hooks/useIsServerSideRendered';
import { usePrevious } from '@/hooks/usePrevious';
import { useAppSelector } from '@/redux/hooks';
import { getAuthFailedCardIds } from '@/redux/modules/registerForCatalog';
import noop from '@/utils/noop';

import { BankAccountOnFileWithPlaidVerifyContainer } from '../Plaid/BankAccountOnFileWithPlaidVerifyContainer';

// Cards are editable only when both handlers are given: hammer-ui renders "Set as Default" on every
// editable non-default card, so `deleteCard` alone would ship a button that does nothing.
type CardEditingProps =
    | {
          deleteCard?: null;
          setDefaultCard?: undefined;
      }
    | {
          deleteCard: (cardId: number) => void;
          setDefaultCard: (creditCard: CreditCard) => void;
      };

type Props = {
    backgroundColor?: string;
    backgroundColorSelected?: string;
    bankAccounts?: BankAccount[];
    borderColor?: string;
    creditCards?: CreditCard[];
    deleteBankAccount?: (bankAccountId: number) => void;
    paymentError?: ReactNode;
    selectBankAccount?: (bankAccount: BankAccount) => void;
    selectCard?: (creditCard: CreditCard) => void;
    selectedCard?: CreditCard;
    title?: ReactNode;
    validationError?: boolean;
} & CardEditingProps;

export const PaymentMethodSelection = ({
    bankAccounts,
    creditCards,
    deleteBankAccount,
    deleteCard,
    paymentError,
    selectBankAccount = noop,
    selectCard = noop,
    selectedCard = null,
    setDefaultCard,
    title = null,
}: Props) => {
    const authFailedCardIds: number[] = useAppSelector((state) => getAuthFailedCardIds(state));
    const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<string>(
        selectedCard ? `card-${selectedCard?.id}` : ''
    );
    const prevSelectedCard = usePrevious(selectedCard);
    const prevCardsLength = usePrevious(creditCards?.length);
    const prevBankAccountsLength = usePrevious(bankAccounts?.length);
    const isSSR = useIsServerSideRendered();

    useEffect(() => {
        if (
            !selectedPaymentMethodId ||
            prevCardsLength !== creditCards?.length ||
            prevSelectedCard?.id !== selectedCard?.id ||
            prevBankAccountsLength !== bankAccounts?.length
        ) {
            if (creditCards) {
                const defaultCardOnFile = creditCards.find((cof) => cof.defaultCard);
                setSelectedPaymentMethodId(selectedCard ? `card-${selectedCard?.id}` : `card-${defaultCardOnFile?.id}`);
                //just to make sure the parent is on the same page.
                if (!selectedPaymentMethodId && Boolean(defaultCardOnFile?.id) && selectCard) {
                    selectCard(defaultCardOnFile);
                }
            } else if (bankAccounts) {
                const defaultBankAccount = bankAccounts.find((bankAccount) => bankAccount.defaultAccount);
                setSelectedPaymentMethodId(`bankAccount-${defaultBankAccount?.bankAccountId}`);
                //just to make sure the parent is on the same page.
                if (defaultBankAccount && selectBankAccount) {
                    selectBankAccount(defaultBankAccount);
                }
            }
        }
    }, [
        bankAccounts,
        creditCards,
        prevBankAccountsLength,
        prevCardsLength,
        selectBankAccount,
        selectCard,
        selectedPaymentMethodId,
        selectedCard,
        prevSelectedCard?.id,
    ]);

    const mapRadio = useCallback(
        (id: number, radioId: string, selected: boolean, disabled: boolean, child: ReactNode, onChange: () => void) => {
            return (
                <Radio.Option
                    checked={selected}
                    className="mb-md w-full items-start pt-xs pl-[25px] [&_input]:mt-[36px]"
                    disabled={disabled}
                    key={id}
                    name="paymentMethod"
                    onChange={onChange}
                    value={radioId}
                >
                    {child}
                </Radio.Option>
            );
        },
        []
    );

    const creditCardRadios = useMemo(() => {
        if (!creditCards) {
            return null;
        }
        // const sortedcreditCards = creditCards.sort((cardA, cardB) => {
        //     //TODO add created to the type
        //     return new Date(cardB.created).getTime() - new Date(cardA.created).getTime();
        // });
        return creditCards.map((creditCard) => {
            const radioId = `card-${creditCard.id}`;
            const selected = selectedPaymentMethodId === radioId;
            const failedAuth = authFailedCardIds.includes(creditCard.id);
            const label = isSSR ? (
                <div className="flex h-[98px] items-center justify-center pb-md">
                    <Loading />
                </div>
            ) : (
                <div data-testid="cardOnFile">
                    <CreditCardOnFile
                        brand={creditCard.brand}
                        expiration={`${creditCard.month}/${creditCard.year}`}
                        isDefault={creditCard.defaultCard}
                        isEditable={Boolean(deleteCard)}
                        lastFour={creditCard.last4}
                        onRemoveClicked={() => {
                            // Keep the selection as is. Clearing it re-runs the sync effect while the removed card
                            // is still in the list, which auto-selects it and sends a request re-enabling it.
                            deleteCard?.(creditCard.id);
                        }}
                        onSetAsDefaultClicked={() => {
                            setSelectedPaymentMethodId(radioId);
                            setDefaultCard?.(creditCard);
                        }}
                    />
                </div>
            );
            return mapRadio(creditCard.id, radioId, selected, failedAuth, label, () => {
                setSelectedPaymentMethodId(radioId);
                selectCard(creditCard);
            });
        });
    }, [
        creditCards,
        selectedPaymentMethodId,
        authFailedCardIds,
        isSSR,
        deleteCard,
        setDefaultCard,
        mapRadio,
        selectCard,
    ]);

    const bankAccountRadios = useMemo(() => {
        if (!bankAccounts) {
            return null;
        }
        return bankAccounts.map((bankAccount) => {
            const radioId = `bankAccount-${bankAccount.bankAccountId}`;
            const selected = selectedPaymentMethodId === radioId;
            return mapRadio(
                bankAccount.bankAccountId,
                radioId,
                selected,
                bankAccount.status === BankAccountStatus.Pending ||
                    bankAccount.status === BankAccountStatus.PendingManual ||
                    bankAccount.status === BankAccountStatus.PendingAutomatic,
                <BankAccountOnFileWithPlaidVerifyContainer
                    bankAccount={bankAccount}
                    deleteAccount={
                        deleteBankAccount
                            ? () => {
                                  if (selected) {
                                      setSelectedPaymentMethodId(undefined);
                                  }
                                  deleteBankAccount(bankAccount.bankAccountId);
                              }
                            : undefined
                    }
                />,
                () => {
                    setSelectedPaymentMethodId(radioId);
                    selectBankAccount(bankAccount);
                }
            );
        });
    }, [bankAccounts, deleteBankAccount, mapRadio, selectBankAccount, selectedPaymentMethodId]);

    return (
        <>
            {title && (
                <H3
                    className="mt-[10px]"
                    color="secondary"
                >
                    {title}
                </H3>
            )}
            <div>
                {/* Not using Radios component here so we can fully control it ourselves. */}
                {creditCardRadios}
                {bankAccountRadios}
                {Boolean(paymentError) && (
                    <Callout
                        className="mt-sm"
                        message={paymentError}
                        status="critical"
                    />
                )}
            </div>
        </>
    );
};
